import { ArrowDownTrayIcon, CheckCircleIcon, ClockIcon, CloudArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSharePointConfigured } from '../../config/sharepoint';
import { documentDownloadUrl } from '../../data/documents';
import { describeLocation } from '../../data/graph';
import { type SaveContext, loadSaveContext } from '../../data/saveDestination';
import { useDuration } from '../../hooks/useDuration';
import type { GenerationResult } from '../../data/types';
import { showSaveToSharePointModal } from '../sharepoint/showSaveToSharePointModal';

/** The nine pipeline phases, in the order the runner executes them. */
const PHASES = [
  'initialization',
  'template-download',
  'property-processing',
  'image-loading',
  'document-loading',
  'data-preparation',
  'templater-processing',
  'post-processing',
  'upload'
];

interface GenerationOverlayProps {
  running: boolean;
  result: GenerationResult | null;
  /** DC-05: raw backend log lines are for operators, not end users. */
  showFullLog: boolean;
  /** A save or a download went through, so the draft this document came from is no longer needed. */
  onDelivered: () => void;
  onClose: () => void;
}

const quietLink =
  'inline-flex items-center gap-1.5 self-start rounded text-xs text-muted-foreground underline underline-offset-2 transition hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

/**
 * DC-04: phases are reported from the response's real `phaseTiming`, not animated
 * on a timer. A phase with no recorded timing simply has not run.
 */
export function GenerationOverlay({ running, result, showFullLog, onDelivered, onClose }: GenerationOverlayProps) {
  const { t } = useTranslation();
  const { fromSeconds, fromMinutes } = useDuration();
  const [saveContext, setSaveContext] = useState<SaveContext | null>(null);

  const timing = result?.phaseTiming ?? {};
  const warnings = result?.logs.filter((log) => log.level === 'warning') ?? [];
  // `valid` only reflects logged errors, so a property can fail while the run stays valid.
  const failed = result?.propertyResults.filter((property) => property.status === 'failed') ?? [];
  const succeeded = result ? result.valid && failed.length === 0 : false;
  // DC-13. Null means the run was never timed, which is not the same as saving nothing.
  const saved = result?.timeSavedSeconds ?? null;

  const documentId = succeeded ? (result?.documentId ?? null) : null;

  useEffect(() => {
    if (!documentId || !isSharePointConfigured) return;
    let cancelled = false;

    // Without it only Download is offered, which is still a working way out.
    loadSaveContext(documentId)
      .then((loaded) => {
        if (!cancelled) setSaveContext(loaded);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [documentId]);

  // Names the place the template points to; the picker explains itself if the user cannot reach it.
  const primaryLabel = !saveContext
    ? t('wizard.generate.saveToOneDrive')
    : saveContext.saveToCompanyTeamSite && saveContext.companyGroupId && saveContext.companyName
      ? t('wizard.generate.saveToTeamSite', { company: saveContext.companyName })
      : saveContext.templateLocation
        ? t('wizard.generate.saveToLocation', { location: describeLocation(saveContext.templateLocation) })
        : t('wizard.generate.saveToOneDrive');

  const canSave = isSharePointConfigured && saveContext !== null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-panel-dark/60 p-4">
      <div role="dialog" aria-modal="true" aria-label={t('wizard.generate.title')} className="w-full max-w-lg rounded-lg border border-border bg-surface p-6">
        <h2 className="text-base font-semibold text-foreground">
          {running ? t('wizard.generate.running') : t('wizard.generate.title')}
        </h2>

        {/* The phase list is a table of timings; this is the part a screen reader hears. */}
        <p aria-live="polite" className="sr-only">
          {running
            ? t('wizard.generate.announceRunning', { count: Object.keys(timing).length })
            : result
              ? succeeded
                ? t('wizard.generate.announceDone')
                : t('wizard.generate.announceFailed')
              : ''}
        </p>

        <ol className="mt-4 grid gap-1.5">
          {PHASES.map((phase) => {
            const elapsed = timing[phase];
            const done = elapsed !== undefined;

            return (
              <li key={phase} className="flex items-center justify-between text-sm">
                <span className={done ? 'text-foreground' : 'text-muted-foreground'}>
                  {t(`wizard.phases.${phase}`)}
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  {done ? `${elapsed} ms` : running ? '…' : '—'}
                </span>
              </li>
            );
          })}
        </ol>

        {result && !running ? (
          <div className="mt-5">
            {succeeded ? (
              <p className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircleIcon className="size-5 text-accent-foreground" aria-hidden="true" />
                {t('wizard.generate.done', { ms: result.totalTimeMs })}
              </p>
            ) : (
              <div role="alert" className="border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
                <p className="font-semibold">
                  {result.valid ? t('wizard.generate.incomplete') : t('wizard.generate.failed')}
                </p>
                <ul className="mt-1 grid gap-1">
                  {failed.map((property) => (
                    <li key={property.propertyName}>
                      {t('wizard.generate.propertyProblem', {
                        name: property.propertyName,
                        reason: property.errorMessage ?? ''
                      })}
                    </li>
                  ))}
                  {failed.length === 0 ? <li>{t('wizard.generate.genericProblem')}</li> : null}
                </ul>
              </div>
            )}

            {succeeded && saved !== null ? (
              <div className="mt-4 rounded-md border border-border bg-surface p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ClockIcon className="size-4" aria-hidden="true" />
                  {saved > 0 ? t('wizard.generate.timeSavedLabel') : t('wizard.generate.timeOverLabel')}
                </div>
                <p className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
                  {fromSeconds(Math.abs(saved))}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t(saved > 0 ? 'wizard.generate.timeSaved' : 'wizard.generate.timeOver', {
                    actual: fromSeconds(result.completionSeconds ?? 0),
                    estimate: fromMinutes(result.estimatedManualMinutes)
                  })}
                </p>
              </div>
            ) : null}

            {warnings.length > 0 ? (
              <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
                <ExclamationTriangleIcon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <div>
                  <p className="font-semibold">{t('wizard.generate.warnings', { count: warnings.length })}</p>
                  <ul className="mt-1 grid gap-1">
                    {warnings.map((log, index) => (
                      <li key={index}>{log.message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}

            {showFullLog ? (
              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  {t('wizard.generate.fullLog')}
                </summary>
                <ul className="mt-2 grid gap-1 rounded border border-border bg-muted p-3 font-mono text-xs">
                  {result.logs.map((log, index) => (
                    <li key={index} className={log.level === 'error' ? 'text-destructive' : 'text-muted-foreground'}>
                      [{log.phase ?? '-'}] {log.message}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}

            {documentId ? (
              <div className="mt-5 flex flex-col gap-2">
                {canSave ? (
                  <>
                    <button
                      type="button"
                      onClick={() => showSaveToSharePointModal(saveContext, 'default', onDelivered)}
                      className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                      <CloudArrowUpIcon className="size-4" aria-hidden="true" />
                      {primaryLabel}
                    </button>
                    <button
                      type="button"
                      onClick={() => showSaveToSharePointModal(saveContext, 'sharepoint', onDelivered)}
                      className="inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                      {t('wizard.generate.saveToSharePoint')}
                    </button>
                    <a href={documentDownloadUrl(documentId)} onClick={onDelivered} className={quietLink}>
                      <ArrowDownTrayIcon className="size-3.5" aria-hidden="true" />
                      {t('common.download')}
                    </a>
                  </>
                ) : (
                  // Nothing to save to, so Download is the only way out and is shown as a normal action.
                  <a
                    href={documentDownloadUrl(documentId)}
                    onClick={onDelivered}
                    className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    <ArrowDownTrayIcon className="size-4" aria-hidden="true" />
                    {t('common.download')}
                  </a>
                )}
              </div>
            ) : null}

            <button
              type="button"
              onClick={onClose}
              className="mt-5 inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-muted"
            >
              {t('common.close')}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
